// src/utils/sessionColours.js

function hexToRgb(hex) {
  const h = String(hex || "")
    .replace("#", "")
    .trim();
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (![r, g, b].every((v) => Number.isFinite(v))) return null;
  return { r, g, b };
}

function rgbToHex({ r, g, b }) {
  const to = (v) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

// Simple HSL generator -> RGB (good enough for chart series)
function hslToRgb(h, s, l) {
  // h: 0..360, s/l: 0..1
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (h % 360) / 60;
  const X = C * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (0 <= hp && hp < 1) [r1, g1, b1] = [C, X, 0];
  else if (1 <= hp && hp < 2) [r1, g1, b1] = [X, C, 0];
  else if (2 <= hp && hp < 3) [r1, g1, b1] = [0, C, X];
  else if (3 <= hp && hp < 4) [r1, g1, b1] = [0, X, C];
  else if (4 <= hp && hp < 5) [r1, g1, b1] = [X, 0, C];
  else if (5 <= hp && hp < 6) [r1, g1, b1] = [C, 0, X];
  const m = l - C / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/**
 * High-contrast session colours:
 * - First few use brand colours (including primary).
 * - After that, generate distinct hues using golden-angle spacing.
 * - No opacity fading; keep lines readable.
 */
export function buildSessionColours(count, primary) {
  const brand = [
    primary || "#51AED1",
    "#EE3132", // brand-red
    "#F37E2B", // brand-orange
    "#6EBE45", // brand-green
    "#7B61FF", // extra purple
    "#00B3A4", // teal
    "#FFB000", // amber
    "#1F77B4", // blue (matplotlib-ish)
  ];

  const n = Math.max(0, count || 0);
  const out = [];

  // Use brand colours first
  for (let i = 0; i < Math.min(n, brand.length); i++) out.push(brand[i]);

  // Generate the rest
  // Golden angle distributes hues nicely: 137.508°
  // Keep saturation high and lightness mid so it’s visible on white/dark.
  const startHue = 210; // bias toward "medical blue" region initially
  for (let i = brand.length; i < n; i++) {
    const hue = (startHue + (i - brand.length) * 137.508) % 360;
    const sat = 0.78; // high
    const light = 0.48; // mid
    out.push(rgbToHex(hslToRgb(hue, sat, light)));
  }

  return out;
}

export function applySessionColoursToLineDatasets(datasets, colours, primary) {
  return (datasets || []).map((ds, i) => {
    const c = colours?.[i] || primary || "#51AED1";
    return {
      ...ds,
      borderColor: ds.borderColor ?? c,
      pointBackgroundColor: ds.pointBackgroundColor ?? c,
      pointBorderColor: ds.pointBorderColor ?? c,
    };
  });
}

export function applySessionColoursToBarDatasets(datasets, colours, primary) {
  return (datasets || []).map((ds, i) => {
    const c = colours?.[i] || primary || "#51AED1";
    return {
      ...ds,
      backgroundColor: ds.backgroundColor ?? c,
      borderColor: ds.borderColor ?? c,
    };
  });
}
